import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';

interface RestaurantePublico {
  id: string;
  name: string;
  slug: string;
  telefone: string | null;
  logo_url: string | null;
}

const RECURSOS = [
  { emoji: '🪑', titulo: 'QR Code por mesa', texto: 'Cada mesa tem seu QR: o cliente escaneia e a comanda já abre identificada — ele só digita o nome.' },
  { emoji: '📋', titulo: 'Pedidos em tempo real', texto: 'O pedido chega na cozinha na hora, com status acompanhado pelo cliente (pedido → preparando → pronto).' },
  { emoji: '🧾', titulo: 'Comandas separadas', texto: 'Conta dividida sem dor de cabeça: cada cliente abre a própria comanda na mesma mesa.' },
  { emoji: '💰', titulo: 'Financeiro', texto: 'Faturamento por período, ticket médio, mais vendidos — o pulso do salão num só painel.' },
  { emoji: '📦', titulo: 'Estoque', texto: 'Controle de produtos e aviso de esgotado direto no cardápio público.' },
  { emoji: '👥', titulo: 'Usuários com perfis', texto: 'Garçom, cozinha e administrador com acessos separados — cada um vê só o que precisa.' },
];

const PASSOS = [
  { numero: '1', titulo: 'Cadastre seu cardápio', texto: 'Produtos, categorias, preços e fotos — tudo pelo painel, sem ajuda de ninguém.' },
  { numero: '2', titulo: 'Imprima os QR codes', texto: 'Gere os QR de cada mesa e imprima ou baixe em PNG para colocar na mesa.' },
  { numero: '3', titulo: 'Cliente escaneia e pede', texto: 'O cardápio abre no celular dele e o pedido chega na sua cozinha em tempo real.' },
];

// T132: a landing não pode prometer plano/preço/limite que o sistema não
// cobra (não existe campo `plano` no schema nem limite aplicado). Enquanto a
// cobrança não existir, os cards mostram o ESCOPO do produto e o preço é
// "Sob consulta" — quem quiser valor combina com a TechUp.
const PLANOS = [
  {
    nome: 'Essencial',
    preco: 'Sob consulta',
    descricao: 'Para começar a digitalizar o salão.',
    destaque: false,
    itens: ['1 restaurante', 'Cardápio digital com fotos', 'QR codes por mesa', 'Pedidos em tempo real'],
  },
  {
    nome: 'Profissional',
    preco: 'Sob consulta',
    descricao: 'Para a operação completa do dia a dia.',
    destaque: true,
    itens: ['Tudo do Essencial', 'Comandas separadas por cliente', 'Financeiro completo', 'Controle de estoque'],
  },
  {
    nome: 'Master',
    preco: 'Sob consulta',
    descricao: 'Para grupos e redes de restaurantes.',
    destaque: false,
    itens: ['Tudo do Profissional', 'Vários restaurantes na mesma conta', 'Painel do operador (visão geral)'],
  },
];

export default function Landing() {
  const [restaurantes, setRestaurantes] = useState<RestaurantePublico[]>([]);
  const [carregandoVitrine, setCarregandoVitrine] = useState(true);

  useEffect(() => {
    api
      .get('/tenants/publicos')
      .then((res) => setRestaurantes(res.data?.data ?? []))
      .catch(() => setRestaurantes([]))
      .finally(() => setCarregandoVitrine(false));
  }, []);

  const linkCardapio = (slug: string) => `/?restaurante=${slug}`;

  return (
    <div className="min-h-screen bg-gray-950 text-gray-100">
      {/* Topo */}
      <header className="sticky top-0 z-40 bg-gray-950/80 backdrop-blur border-b border-gray-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="text-2xl leading-none">🍽️</span>
            <span className="font-extrabold text-lg">
              Prime<span className="text-primary-400">Click</span>
            </span>
          </div>
          <nav className="hidden md:flex items-center gap-6 text-sm text-gray-300">
            <a href="#recursos" className="hover:text-white">Recursos</a>
            <a href="#como-funciona" className="hover:text-white">Como funciona</a>
            <a href="#planos" className="hover:text-white">Planos</a>
            <a href="#restaurantes" className="hover:text-white">Restaurantes</a>
          </nav>
          <Link to="/login" className="btn-primary text-sm">
            Entrar
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary-600/10 via-transparent to-transparent" aria-hidden />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-20 sm:py-28 text-center">
          <p className="inline-flex items-center gap-2 text-xs font-medium text-primary-300 bg-primary-600/10 border border-primary-600/30 rounded-full px-4 py-1.5 mb-6">
            ✨ Do QR Code ao pedido na mesa
          </p>
          <h1 className="text-4xl sm:text-6xl font-extrabold leading-tight tracking-tight">
            Seu restaurante com{' '}
            <span className="text-primary-400">cardápio digital</span>
            <br className="hidden sm:block" /> em minutos
          </h1>
          <p className="mt-6 text-lg text-gray-400 max-w-2xl mx-auto">
            O cliente escaneia o QR da mesa, vê o cardápio no celular e pede —
            sem app, sem garçom anotando, sem espera. Você acompanha tudo em
            tempo real no painel.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <a href="#planos" className="btn-primary text-base px-8 py-3">
              Ver planos
            </a>
            <Link to="/login" className="btn-secondary text-base px-8 py-3">
              Entrar no painel
            </Link>
          </div>
          <div className="mt-14 flex flex-wrap items-center justify-center gap-8 text-sm text-gray-400">
            <div>
              <p className="text-2xl font-bold text-gray-100">
                {carregandoVitrine ? '…' : restaurantes.length}
              </p>
              <p>restaurantes ativos</p>
            </div>
            <div className="text-gray-700 hidden sm:block" aria-hidden>•</div>
            <div>
              <p className="text-2xl font-bold text-gray-100">∞</p>
              <p>QR codes por mesa</p>
            </div>
            <div className="text-gray-700 hidden sm:block" aria-hidden>•</div>
            <div>
              <p className="text-2xl font-bold text-gray-100">Real</p>
              <p>tempo nos pedidos</p>
            </div>
          </div>
        </div>
      </section>

      {/* Recursos */}
      <section id="recursos" className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
        <h2 className="text-3xl font-bold text-center">Tudo que seu restaurante precisa</h2>
        <p className="text-center text-gray-400 mt-2 mb-10">
          Um sistema único: o cliente pede pelo celular, você controla pelo painel.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {RECURSOS.map((r) => (
            <div key={r.titulo} className="card p-6 hover:border-primary-500/50 transition-colors">
              <div className="text-3xl mb-3">{r.emoji}</div>
              <h3 className="font-bold text-lg text-gray-100">{r.titulo}</h3>
              <p className="text-sm text-gray-400 mt-1.5 leading-relaxed">{r.texto}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Como funciona */}
      <section id="como-funciona" className="bg-gray-900/50 border-y border-gray-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
          <h2 className="text-3xl font-bold text-center">Como funciona</h2>
          <p className="text-center text-gray-400 mt-2 mb-10">
            Em três passos seu salão está digital.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PASSOS.map((p) => (
              <div key={p.numero} className="text-center">
                <div className="w-12 h-12 mx-auto rounded-full bg-primary-600/15 border border-primary-600/40 text-primary-400 font-bold text-lg flex items-center justify-center mb-4">
                  {p.numero}
                </div>
                <h3 className="font-bold text-lg">{p.titulo}</h3>
                <p className="text-sm text-gray-400 mt-1.5 leading-relaxed">{p.texto}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Planos */}
      <section id="planos" className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
        <h2 className="text-3xl font-bold text-center">Escolha o tamanho da sua operação</h2>
        <p className="text-center text-gray-400 mt-2 mb-10">
          Valores sob consulta: fale com a gente e monte o plano do seu salão.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PLANOS.map((plano) => (
            <div
              key={plano.nome}
              className={`card p-6 flex flex-col ${
                plano.destaque
                  ? 'border-primary-500/60 ring-1 ring-primary-500/40'
                  : 'hover:border-gray-600 transition-colors'
              }`}
            >
              {plano.destaque && (
                <span className="self-start text-[11px] font-semibold uppercase tracking-wide bg-primary-600 text-white rounded-full px-3 py-1 mb-3">
                  Destaque
                </span>
              )}
              <h3 className="text-lg font-bold">{plano.nome}</h3>
              <p className="text-sm text-gray-400">{plano.descricao}</p>
              <p className="mt-4 text-2xl font-extrabold text-gray-200">
                {plano.preco}
              </p>
              <ul className="mt-5 space-y-2 text-sm flex-1">
                {plano.itens.map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <span className="text-primary-400 mt-0.5">✓</span>
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                to="/login"
                className={`mt-6 ${plano.destaque ? 'btn-primary' : 'btn-secondary'} text-center`}
              >
                Começar agora
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* Restaurantes ativos */}
      <section id="restaurantes" className="bg-gray-900/50 border-t border-gray-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
          <h2 className="text-3xl font-bold text-center">Cardápios ativos agora</h2>
          <p className="text-center text-gray-400 mt-2 mb-10">
            Veja na prática como fica o cardápio dos restaurantes que já usam a plataforma.
          </p>
          {carregandoVitrine ? (
            <div className="text-center text-gray-500 py-8">Carregando...</div>
          ) : restaurantes.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              Em breve — o primeiro restaurante ativo aparece aqui.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {restaurantes.map((r) => (
                <div key={r.id} className="card p-5 flex flex-col">
                  <div className="flex items-center gap-3">
                    {r.logo_url ? (
                      <img
                        src={r.logo_url}
                        alt={r.name}
                        className="w-11 h-11 rounded-xl object-cover border border-gray-700 shrink-0"
                      />
                    ) : (
                      <div className="w-11 h-11 rounded-xl bg-primary-600/15 border border-primary-600/30 flex items-center justify-center text-xl shrink-0">
                        🏪
                      </div>
                    )}
                    <div className="min-w-0">
                      <h3 className="font-bold truncate">{r.name}</h3>
                      <p className="text-xs text-gray-500 truncate">
                        {r.telefone || 'cardápio digital'}
                      </p>
                    </div>
                  </div>
                  <Link
                    to={linkCardapio(r.slug)}
                    className="btn-secondary text-sm mt-4 text-center"
                  >
                    Ver cardápio →
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Chamada final */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 text-center">
        <h2 className="text-3xl font-bold">Pronto para digitalizar seu salão?</h2>
        <p className="text-gray-400 mt-3 mb-8">
          Crie seu cardápio em minutos — sem instalação, sem contrato.
        </p>
        <Link to="/login" className="btn-primary text-base px-10 py-3">
          Começar agora
        </Link>
      </section>

      {/* Rodapé */}
      <footer className="border-t border-gray-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-500">
          <div className="flex items-center gap-2">
            <span className="text-xl">🍽️</span>
            <span>
              Prime<span className="text-primary-400 font-semibold">Click</span>
            </span>
          </div>
          <p>© {new Date().getFullYear()} · Todos os direitos reservados</p>
          <Link to="/admin" className="hover:text-gray-300">
            🛠️ Operador
          </Link>
        </div>
      </footer>
    </div>
  );
}